import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Database, LayoutDashboard, Users } from 'lucide-react';
import monitoringDashboardImage from '@/assets/landing/monitoring-dashboard.svg';
import monitoringDashboardHoveredImage from '@/assets/landing/monitoring-dashboard-hovered.svg';
import rolePermissionImage from '@/assets/landing/role-permission.svg';
import rolePermissionHoveredImage from '@/assets/landing/role-permission-hovered.svg';
import structuredClientDataImage from '@/assets/landing/structured-client-data.svg';
import structuredClientDataHoveredImage from '@/assets/landing/structured-client-data-hovered.svg';
import { LocalizedProductPreview } from './localized-product-preview';

interface TeamCardProps {
  icon: React.ElementType;
  title: string;
  body: string;
  svgDefault: string;
  svgHovered: string;
  className?: string;
}

function TeamCard({
  icon: Icon,
  title,
  body,
  svgDefault,
  svgHovered,
  className = '',
}: TeamCardProps) {
  const locale = useLocale();

  return (
    <div
      className={`group flex flex-col overflow-hidden rounded-2xl border border-landing-card-border bg-landing-active-card-bg p-6 transition-all duration-300 hover:border-primary/30 ${className}`}
    >
      <Icon className="mb-3 h-5 w-5 shrink-0 text-primary" />
      <p className="mb-1 font-bold font-libre-baskerville text-landing-heading-sm text-sm leading-snug">
        {title}
      </p>
      <p className="mb-5 text-landing-body text-xs leading-relaxed">{body}</p>

      {/* SVG illustration area — fade between default and hovered */}
      <div className="relative mt-auto overflow-hidden bg-transparent">
        {locale === 'fa' ? (
          <LocalizedProductPreview compact title={title} />
        ) : (
          <>
            <img
              alt={title}
              className="block w-full opacity-100 transition-opacity duration-300 group-hover:opacity-0"
              src={svgDefault}
            />
            {/* Hovered SVG — absolutely overlaid, fades in */}
            <img
              alt={title}
              className="absolute inset-0 w-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              src={svgHovered}
            />
          </>
        )}
      </div>
    </div>
  );
}

export const TeamCollaborationSection = ({ className }: { className?: string }) => {
  useLocale();

  return (
    <section className={className}>
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        {/* Header */}
        <h2 className="mb-3 font-bold font-libre-baskerville text-4xl text-landing-heading">
          {t('landing.teamCollaborationOperations')}{' '}
        </h2>
        <p className="mb-10 text-landing-body text-sm leading-relaxed">
          {t('landing.toolsToStreamlineTeamworkTaskManagementAndClientOversight')}{' '}
        </p>

        {/* Grid: 1 large full-width card + 2 half-width cards below */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Card 1 — full width */}
          <TeamCard
            body={t('extra.defineCustomRolesAndControlAccessLevelsForEveryTeam')}
            className="md:col-span-2"
            icon={Users}
            svgDefault={rolePermissionImage}
            svgHovered={rolePermissionHoveredImage}
            title={t('landing.rolePermissionManagement')}
          />

          {/* Card 2 */}
          <TeamCard
            body={t('extra.trackClientsDataReportsAndTeamActivityAllInOne')}
            icon={LayoutDashboard}
            svgDefault={monitoringDashboardImage}
            svgHovered={monitoringDashboardHoveredImage}
            title={t('landing.unifiedMonitoringDashboard')}
          />

          {/* Card 3 */}
          <TeamCard
            body={t('extra.accessWellOrganizedClientProfilesReportsAndForSearchAnd')}
            icon={Database}
            svgDefault={structuredClientDataImage}
            svgHovered={structuredClientDataHoveredImage}
            title={t('landing.structuredClientDataHistory')}
          />
        </div>
      </div>
    </section>
  );
};
